/**
 * VerifierAgreementPage
 *
 * Shows the Field Verifier Agreement text, a typed-name signature field,
 * and an "Agree & Sign" button that records the e-signature.
 *
 * Route: /verifier/agreement  (authenticated, verifier role)
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircle, Gavel } from '@mui/icons-material';
import verifierAgreementService from '../services/verifierAgreementService';
import { brand, text, bg, border } from '../theme/colors';

export default function VerifierAgreementPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [agreement, setAgreement] = useState(null);

  // Form state
  const [fullName, setFullName] = useState('');
  const [agreed, setAgreed] = useState(false);

  // Load agreement on mount
  useEffect(() => {
    (async () => {
      try {
        const data = await verifierAgreementService.getAgreement();
        setAgreement(data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load agreement.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSign = async () => {
    if (!fullName.trim()) {
      setError('Please type your full name to sign.');
      return;
    }
    if (!agreed) {
      setError('Please check the box to confirm you have read and agree to the agreement.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const result = await verifierAgreementService.acceptAgreement(fullName.trim());
      // Refresh agreement state to show accepted banner
      setAgreement((prev) => ({
        ...prev,
        accepted: true,
        acceptedAt: result.acceptedAt,
      }));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to record your signature. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!agreement && error) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const { version, title, sections, accepted, acceptedAt } = agreement || {};

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Gavel sx={{ color: brand.primary, fontSize: '2rem' }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: text.primary, lineHeight: 1.2 }}>
            {title}
          </Typography>
          <Typography sx={{ color: text.secondary, fontSize: '0.85rem' }}>
            Version {version}
          </Typography>
        </Box>
      </Box>

      {/* Already-accepted banner */}
      {accepted && (
        <Alert
          severity="success"
          icon={<CheckCircle />}
          sx={{ mb: 4, borderRadius: 2 }}
        >
          <strong>Agreement accepted.</strong>
          {acceptedAt && (
            <> Signed on {new Date(acceptedAt).toLocaleDateString('en-ZW', { year: 'numeric', month: 'long', day: 'numeric' })} (v{version})</>
          )}
          <Box sx={{ mt: 1 }}>
            <Button size="small" onClick={() => navigate('/verifier/my-work')}>
              Go to My Work
            </Button>
          </Box>
        </Alert>
      )}

      {/* Duties / Job Spec — what the verifier actually does, shown with the terms */}
      <Paper
        elevation={0}
        sx={{ border: `1px solid ${border.default}`, borderRadius: 3, p: { xs: 3, md: 4 }, mb: 4, bgcolor: bg.paper }}
      >
        <Typography sx={{ fontWeight: 800, color: text.primary, mb: 0.5, fontSize: '1.05rem' }}>
          Your duties as a Field Verifier
        </Typography>
        <Typography sx={{ color: text.secondary, fontSize: '0.85rem', mb: 2 }}>
          For each assignment you complete these steps in the Eqaya app:
        </Typography>
        {[
          ['Identity & authority check', "Confirm the contractor representative's identity and their authority to act on behalf of the business."],
          ['Client & trade references', 'Contact at least two independent references — past clients or trade contacts — and record their feedback.'],
          ['GPS-stamped site visits', 'Physically attend at least two portfolio sites, verify the work was done, and capture GPS-tagged photo evidence.'],
          ['Portfolio review', "Check the contractor's stated portfolio against what you observe on-site and flag any inconsistencies."],
          ['Structured report & recommendation', 'Submit a complete report in the app with an approve or reject recommendation and supporting evidence.'],
        ].map(([t, d], i) => (
          <Box key={i} sx={{ mb: 1.5 }}>
            <Typography sx={{ fontWeight: 700, color: text.primary, fontSize: '0.9rem' }}>{i + 1}. {t}</Typography>
            <Typography sx={{ color: text.secondary, fontSize: '0.88rem', lineHeight: 1.6 }}>{d}</Typography>
          </Box>
        ))}
      </Paper>

      {/* Agreement text */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${border.default}`,
          borderRadius: 3,
          p: { xs: 3, md: 4 },
          mb: 4,
          bgcolor: bg.paper,
          maxHeight: 520,
          overflowY: 'auto',
        }}
      >
        {sections?.map((section, idx) => (
          <Box key={idx} sx={{ mb: 3 }}>
            <Typography
              sx={{ fontWeight: 700, color: text.primary, mb: 0.75, fontSize: '0.95rem' }}
            >
              {idx + 1}. {section.heading}
            </Typography>
            <Typography sx={{ color: text.secondary, lineHeight: 1.75, fontSize: '0.9rem' }}>
              {section.body}
            </Typography>
            {idx < sections.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        ))}
      </Paper>

      {/* Signature form — only shown when not yet accepted */}
      {!accepted && (
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${border.default}`,
            borderRadius: 3,
            p: { xs: 3, md: 4 },
            bgcolor: bg.paper,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: text.primary, mb: 1 }}>
            Sign this agreement
          </Typography>
          <Typography sx={{ color: text.secondary, mb: 3, fontSize: '0.9rem', lineHeight: 1.65 }}>
            Type your full legal name below as your electronic signature. This has the same legal
            effect as a handwritten signature under the Electronic Transactions Act [Chapter 22:05]
            of Zimbabwe.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <TextField
            label="Full legal name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            fullWidth
            variant="outlined"
            inputProps={{ maxLength: 120 }}
            placeholder="e.g. Tendai Moyo"
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                sx={{ color: brand.primary, '&.Mui-checked': { color: brand.primary } }}
              />
            }
            label={
              <Typography sx={{ fontSize: '0.9rem', color: text.secondary, lineHeight: 1.5 }}>
                I have read the full agreement above and agree to be bound by its terms.
              </Typography>
            }
            sx={{ mb: 3, alignItems: 'flex-start' }}
          />

          <Button
            variant="contained"
            size="large"
            onClick={handleSign}
            disabled={submitting || !fullName.trim() || !agreed}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircle />}
            sx={{
              bgcolor: brand.primary,
              color: 'white',
              fontWeight: 700,
              textTransform: 'none',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              '&:hover': { bgcolor: brand.primaryDark },
              '&:disabled': { opacity: 0.6 },
            }}
          >
            {submitting ? 'Signing…' : 'Agree & Sign'}
          </Button>
        </Paper>
      )}
    </Container>
  );
}
